import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Section from "components/front/project/layout/Section";
import SsHeadline from "components/front/layout/SsHeadline";
import styled from "styled-components";
import { ProjectActivity } from "interfaces/front/business";
import BottomSlide from "components/front/project/activity/BottomSlide";
import ActivityItem from "components/front/project/activity/ActivityItem";
import useContentSwiper from "lib/hooks/useContentSwiper";

export type Props = {
  activities: ProjectActivity[];
};
const ProjectActivitySection: FC<Props> = ({ activities }) => {
  const { showsSection, seesMore, toggleSeeMore, seeMoreText, laterActivities, otherActivities, openedIndex, toggleOpenedIndex } =
    useProjectActivitySectionStates(activities);
  const imageHolderRef = useRef<HTMLDivElement>(null);
  const loadSwiper = useContentSwiper(imageHolderRef)
  useEffect(() => {
    loadSwiper();
  }, [])
  return showsSection ? (
    <Section ref={imageHolderRef}>
      <SsHeadline label="最新情報" color="white" />
      <ActivityList>
        {laterActivities.map((activity, index) => (
          <ActivityItem
            key={index}
            activity={activity}
            firstRow={index === 0}
            selected={openedIndex == activity.index}
            onSelect={() => toggleOpenedIndex(activity.index)}
          />
        ))}
      </ActivityList>
      <BottomSlide<HTMLUListElement> in={seesMore}>
        {({ container, onChildHeightChange }) => (
          <ActivityList ref={container}>
            {otherActivities.map((activity, index) => (
              <ActivityItem
                key={index}
                activity={activity}
                selected={openedIndex == activity.index}
                onSelect={() => toggleOpenedIndex(activity.index)}
                onSlide={onChildHeightChange}
              />
            ))}
          </ActivityList>
        )}
      </BottomSlide>
      {otherActivities.length > 0 && (
        <SeeMoreButton onClick={toggleSeeMore}>{seeMoreText}</SeeMoreButton>
      )}
    </Section>
  ) : (
    <></>
  );
};
export default ProjectActivitySection;

const useProjectActivitySectionStates = (activities: ProjectActivity[]) => {
  const showsSection = useMemo(() => activities.length > 0, [activities]);
  const [seesMore, setSeeMore] = useState(false);
  const toggleSeeMore = useCallback(() => setSeeMore(!seesMore), [seesMore]);
  const seeMoreText = useMemo(() => (seesMore ? "隠す" : "もっと見る"), [seesMore]);
  const [laterActivities, otherActivities] = useMemo(
    () =>
      activities.reduce(
        (result, activity, index) => {
          result[index >= 2 ? 1 : 0].push({ ...activity, index });
          return result;
        },
        [[] as IndexedActivity[], [] as IndexedActivity[]]
      ),
    [activities]
  );
  const [openedIndex, setOpenedIndex] = useState<number>();
  const toggleOpenedIndex = useCallback(
    (index: number) => {
      const nextIndex = openedIndex == index ? undefined : index;
      setOpenedIndex(nextIndex);
    },
    [openedIndex]
  );
  return { showsSection, seesMore, toggleSeeMore, seeMoreText, laterActivities, otherActivities, openedIndex, toggleOpenedIndex };
};

type IndexedActivity = ProjectActivity & {
  index: number;
};

const ActivityList = styled.ul`
  width: 100%;
  @media screen and (max-width: 960px) {
    font-size: 14px;
  }
  @media screen and (min-width: 961px) {
    font-size: 16px;
  }
`;

const SeeMoreButton = styled.div`
  cursor: pointer;
  margin-top: 40px;
  text-align: center;
  text-decoration: underline;
  @media screen and (max-width: 960px) {
    font-size: 12px;
  }
  @media screen and (max-width: 960px) {
    font-size: 14px;
    margin-top: 20px;
  }
`;
